import {get, last} from 'lodash'
import {accountTypes} from '@bdswiss/common-enums'
import {isTauroFactorisedAccount} from '../../../common/utils/accounts'

type TauroFactorisationRules = {
  enabled?: boolean
  multiplier?: number
  leverageDivisor?: number
  drawdownTriggerPercent?: number
  tauroProfitSplitPercent?: number
  minQualifyingDeposit?: number
  maxQualifyingDeposit?: number
  maxActiveAccounts?: number
  maxLifetimeAccounts?: number
  canOpen?: boolean
  configurations?: TauroFactorisationRules[]
}

/**
 * The campaign configuration, or the selected entry of its multiplier list. The server-resolved
 * viewer field wins; the latest registration campaign remains the legacy fallback.
 */
export const getViewerTauroFactorisation = (viewer: unknown, multiplier?: number): TauroFactorisationRules | null => {
  const campaign = get(last(get(viewer, 'registrationCampaigns') || []), 'campaign')
  const tauro = (get(viewer, 'tauroFactorisation') ?? get(campaign, 'tauroFactorisation')) as
    | TauroFactorisationRules
    | undefined
  if (!tauro || tauro.enabled !== true) return null
  if (multiplier == null || !tauro.configurations) return tauro
  const configuration = tauro.configurations.find((entry) => entry.multiplier === multiplier)
  return configuration ? {...tauro, ...configuration} : null
}

/**
 * Whether the account being created will be a Tauro 10x factorised account, and so
 * must carry an explicit Terms & Conditions acceptance before it can be created.
 * Factorisation applies only to live MT5 Classic (MT5_STD) accounts of clients under
 * an enabled 10x campaign; `accountSubtype` is the wizard's raw selection and may be
 * empty while the account type resolves it implicitly.
 */
export const requiresTauroTermsAcceptance = (
  viewer: unknown,
  accountType?: string,
  accountSubtype?: string,
  tauroFactorisationRequested?: boolean,
): boolean => {
  if (!accountType || !accountTypes[accountType]) return false
  // The client picked the plain MT5 Classic option, so the 10x rules do not govern this
  // account and there is nothing for them to accept (PT-6510).
  if (tauroFactorisationRequested === false) return false
  const type = accountTypes[accountType]
  if (type.category !== 'forexMt5' || type.isDemo) return false
  if (accountSubtype && accountSubtype !== 'mt5Std') return false
  return getViewerTauroFactorisation(viewer) != null
}

/**
 * Removes USD Cent (USC) from the currencies offered for a Tauro 10x factorised account
 * (PT-6600). The 10x rules do not apply to cent accounts, so a factorised MT5 Classic must
 * not be opened in USC. The standard MT5 Classic option keeps USC as before.
 */
export const withoutCentCurrencyForFactorised = <T extends {key: string}>(
  currencyList: T[],
  viewer: unknown,
  accountType?: string,
  accountSubtype?: string,
  tauroFactorisationRequested?: boolean,
): T[] =>
  requiresTauroTermsAcceptance(viewer, accountType, accountSubtype, tauroFactorisationRequested)
    ? currencyList.filter((currency) => currency.key !== 'USC')
    : currencyList

/** An entry of the "Add New Account" list, as `getAllowedAccounts` builds it. */
type AllowedAccount = {
  accountType: string
  accountSubtype?: string
  category?: string
  isDemo?: boolean
  tauroFactorised?: boolean
  tauroMultiplier?: number
}

/** True for the live MT5 Classic entry — the only account factorisation applies to. */
const isLiveMt5ClassicEntry = (entry: AllowedAccount): boolean =>
  !entry.isDemo && entry.category === 'forexMt5' && entry.accountSubtype === 'mt5Std'

export const TAURO_FACTORISED_ACCOUNTS_LIFETIME_MAX = 3

/** Default of the active (not archived) factorised accounts a client holds at once per configuration. */
export const TAURO_FACTORISED_ACCOUNTS_ACTIVE_MAX = 1

/** An account of the viewer as the portal lists them. Only live forex accounts carry `isArchived`. */
type FactorisedAccountRow = Parameters<typeof isTauroFactorisedAccount>[0] & {isArchived?: boolean | null}

/** The factorised accounts of the viewer, within one multiplier when given. */
const factorisedAccountsOf = (accounts: FactorisedAccountRow[], multiplier?: number): FactorisedAccountRow[] =>
  accounts
    .filter(isTauroFactorisedAccount)
    .filter((account) =>
      multiplier == null || get(account, 'metadata.tauroFactorisation.multiplier') === multiplier,
    )

/** Active (not archived) factorised accounts of the viewer, within one multiplier when given. */
const countActiveFactorisedAccounts = (accounts: FactorisedAccountRow[], multiplier?: number): number =>
  factorisedAccountsOf(accounts, multiplier).filter((account) => !account.isArchived).length

/**
 * Local eligibility within one multiplier; the server answer remains authoritative. A configuration
 * allows `maxActive` active accounts at a time (PT-8244) and `maxLifetime` accounts in total.
 */
export const canOpenTauroFactorisedAccount = (
  accounts: FactorisedAccountRow[],
  multiplier?: number,
  maxLifetime = TAURO_FACTORISED_ACCOUNTS_LIFETIME_MAX,
  maxActive = TAURO_FACTORISED_ACCOUNTS_ACTIVE_MAX,
): boolean =>
  countActiveFactorisedAccounts(accounts, multiplier) < maxActive &&
  factorisedAccountsOf(accounts, multiplier).length < maxLifetime

/** Split live MT5 Classic into the standard choice and every available multiplier product. */
export const withTauroClassicOptions = <T extends AllowedAccount & {maxAllowed?: number; available?: number | ''}>(
  allowedAccounts: T[],
  viewer: unknown,
  accounts: FactorisedAccountRow[] = [],
): T[] => {
  const tauro = getViewerTauroFactorisation(viewer)
  if (tauro == null) return allowedAccounts
  if (tauro.configurations) {
    const configurations = tauro.configurations.filter(
      (configuration) =>
        configuration.canOpen !== false &&
        canOpenTauroFactorisedAccount(
          accounts,
          configuration.multiplier,
          configuration.maxLifetimeAccounts,
          configuration.maxActiveAccounts,
        ),
    )
    return allowedAccounts.flatMap((entry) =>
      isLiveMt5ClassicEntry(entry)
        ? [
            // Each tile carries the active limit of its configuration and the slots left in it (PT-8244).
            ...configurations.map((configuration) => {
              const maxAllowed = configuration.maxActiveAccounts ?? TAURO_FACTORISED_ACCOUNTS_ACTIVE_MAX
              const activeLeft = maxAllowed - countActiveFactorisedAccounts(accounts, configuration.multiplier)
              const lifetimeLeft =
                (configuration.maxLifetimeAccounts ?? TAURO_FACTORISED_ACCOUNTS_LIFETIME_MAX) -
                factorisedAccountsOf(accounts, configuration.multiplier).length
              const classicLeft = typeof entry.available === 'number' ? entry.available : Infinity
              return {
                ...entry,
                maxAllowed,
                // The server applies the three limits when the account opens, so the tile shows the tightest one.
                available: Math.max(0, Math.min(activeLeft, lifetimeLeft, classicLeft)),
                tauroFactorised: true,
                tauroMultiplier: configuration.multiplier,
              }
            }),
            {...entry, tauroFactorised: false},
          ]
        : [entry],
    )
  }
  const offersFactorised =
    canOpenTauroFactorisedAccount(accounts) && get(viewer, 'canOpenTauroFactorisedAccount') !== false
  return allowedAccounts.flatMap((entry) =>
    isLiveMt5ClassicEntry(entry)
      ? [
          ...(offersFactorised ? [{...entry, maxAllowed: 1, tauroFactorised: true}] : []),
          {...entry, tauroFactorised: false},
        ]
      : [entry],
  )
}
